// utils/eventGroups.js
// Event groups — e.g. "Beginner" and "Advanced" inside one session.
//
//   event.groups      = [{ id, name, courts: [], capacity, price | null }]
//   event.groupCounts = { [groupId]: { taken, reserved, waiting } }   (server cache)
//   registration.groupId / groupName
//
// Mirrors the helpers of the same names in functions/index.js — keep the
// pricing rules identical on both sides or PayPal amount checks will fail.
import { getEarlyBirdStatus } from './eventHelpers';

export const eventGroups = (ev) =>
  (Array.isArray(ev?.groups) ? ev.groups.filter((g) => g && g.id) : []);

export const hasGroups = (ev) => eventGroups(ev).length > 0;

export const findGroup = (ev, groupId) =>
  eventGroups(ev).find((g) => g.id === groupId) || null;

export const groupHasPrice = (g) =>
  !!g && g.price !== null && g.price !== undefined && g.price !== '' && !Number.isNaN(Number(g.price));

/** Base price for a registration in group `g` (group price beats early bird). */
export const groupBasePrice = (ev, g) => {
  if (ev?.isFreeEvent === true) return 0;
  if (groupHasPrice(g)) return Number(g.price);
  return getEarlyBirdStatus(ev).effectivePrice;
};

/** Unit price of a signup option; player tickets follow the group price. */
export const optionUnitPrice = (ev, opt, g) => {
  if (opt?.isPlayerTicket && groupHasPrice(g)) return groupBasePrice(ev, g);
  return Number(opt?.price) || 0;
};

/** Seats for one group from the server-maintained event.groupCounts. */
export const groupStats = (ev, g) => {
  const c = (ev?.groupCounts || {})[g?.id] || {};
  const capacity = Number(g?.capacity) || 0;
  const taken = c.taken || 0;
  const reserved = c.reserved || 0;
  const used = taken + reserved;
  return {
    capacity,
    taken,
    reserved,
    waiting: c.waiting || 0,
    left: Math.max(0, capacity - used),
    full: capacity > 0 && used >= capacity,
  };
};

export const totalGroupCapacity = (groups) =>
  (groups || []).reduce((s, g) => s + (parseInt(g?.capacity, 10) || 0), 0);

export const newGroupId = () => `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/** Two starter groups splitting `total` seats. */
export const defaultGroups = (total = 16) => {
  const half = Math.max(1, Math.floor((parseInt(total, 10) || 16) / 2));
  return [
    { id: newGroupId(), name: '初级组 Beginner', courts: [], capacity: half, price: null },
    { id: newGroupId(), name: '高水平组 Advanced', courts: [], capacity: Math.max(1, (parseInt(total, 10) || 16) - half), price: null },
  ];
};

/** Cleans editor state into what is stored on the event. */
export const normalizeGroups = (groups, { isFreeEvent = false } = {}) =>
  (groups || [])
    .filter((g) => g && (g.name || '').trim())
    .map((g) => ({
      id: g.id || newGroupId(),
      name: g.name.trim().slice(0, 40),
      courts: Array.isArray(g.courts) ? g.courts.map(String) : [],
      capacity: Math.max(1, parseInt(g.capacity, 10) || 1),
      price: isFreeEvent || !groupHasPrice(g) ? null : Math.round(Number(g.price) * 100) / 100,
    }));

/** Returns an error message (zh + en) or '' when the groups are valid. */
export const validateGroups = (groups) => {
  if (!groups || groups.length === 0) return '';
  if (groups.length < 2) return '至少需要 2 个分组 / Add at least 2 groups';
  for (const g of groups) {
    if (!(g.name || '').trim()) return '每个分组都要有名字 / Every group needs a name';
    if (!(parseInt(g.capacity, 10) > 0)) return `「${g.name}」人数至少为 1 / "${g.name}" needs at least 1 spot`;
    if (groupHasPrice(g) && Number(g.price) < 0) return `「${g.name}」价格不能为负 / Price can't be negative`;
  }
  const names = groups.map((g) => g.name.trim().toLowerCase());
  if (new Set(names).size !== names.length) return '分组名字不能重复 / Group names must be different';
  return '';
};

/** Short label for chips, e.g. "Court 1, 2" — courts already named "Court 1" aren't prefixed twice. */
export const courtsLabel = (g) => {
  const cs = (g?.courts || []).map((c) => String(c).trim()).filter(Boolean);
  if (!cs.length) return '';
  if (cs.every((c) => /^court\b/i.test(c))) {
    return `Court ${cs.map((c) => c.replace(/^court\s*/i, '')).join(', ')}`;
  }
  if (cs.some((c) => /^(court|场)/i.test(c))) return cs.join(', ');
  return `Court ${cs.join(', ')}`;
};

// ── Friends ("Additional Player") — each friend can be in their own group ──
// The host's player-ticket option carries guestGroups: [groupId per friend].

/** Group id of friend i (falls back to the player's own group). */
export const friendGroupId = (ev, so, i, hostGroupId) => {
  const gg = Array.isArray(so?.guestGroups) ? so.guestGroups : [];
  return findGroup(ev, gg[i]) ? gg[i] : (hostGroupId || null);
};

/** Price of one selected option line; friends at their own group's price. */
export const optionLineTotal = (ev, so, hostGroup) => {
  const opt = ev?.signupOptions?.[so?.index];
  if (!opt) return 0;
  const q = so.quantity || 1;
  if (opt.isPlayerTicket) {
    let t = 0;
    for (let i = 0; i < q; i++) {
      t += optionUnitPrice(ev, opt, findGroup(ev, friendGroupId(ev, so, i, hostGroup?.id)) || hostGroup);
    }
    return t;
  }
  return optionUnitPrice(ev, opt, hostGroup) * q;
};

/** Seats a booking needs per group: { [groupId]: n } (player + each friend). */
export const bookingGroupNeeds = (ev, hostGroupId, selectedOptions) => {
  const out = {};
  if (!hostGroupId) return out;
  out[hostGroupId] = 1;
  (selectedOptions || []).forEach((so) => {
    const opt = ev?.signupOptions?.[so?.index];
    if (!opt?.isPlayerTicket) return;
    for (let i = 0; i < (so.quantity || 1); i++) {
      const g = friendGroupId(ev, so, i, hostGroupId);
      out[g] = (out[g] || 0) + 1;
    }
  });
  return out;
};

/** First group (as {group, left, need}) the booking doesn't fit in, or null. */
export const groupShortfall = (ev, needs, credit = {}) => {
  for (const gid of Object.keys(needs || {})) {
    const g = findGroup(ev, gid);
    if (!g) continue;
    const left = groupStats(ev, g).left + (credit[gid] || 0);
    if (left < needs[gid]) return { group: g, left, need: needs[gid] };
  }
  return null;
};

/** Friend registrations (their own docs) are flagged isFriend. */
export const isFriendReg = (r) => !!r?.isFriend;
